import * as Yup from "yup";

import {
  PredictorOutcomeAggregation,
  PredictorOutcomeType,
} from "@hightouch/lib/predictive-traits/types";

import type { CreatePredictorInput } from "src/graphql";

import { traitNameValidator } from "src/components/traits/utils";
import { TraitType } from "src/types/visual";
import { filterSchema } from "./components/catalog-filter-schema";

function getPredictorSchema(usingCatalog: boolean, usingLookalike: boolean) {
  const outcomeSchema = Yup.object({
    keyEvent: Yup.object({
      relationshipId: Yup.string().required(),
    }).required(),
    timeframe: Yup.object({
      quantity: Yup.number().required(),
      unit: Yup.string().required(),
      is_churn: Yup.boolean().required(),
    }).required(),
    type: Yup.string().oneOf(Object.values(PredictorOutcomeType)).notRequired(),
    valueColumn: Yup.string().when("type", {
      is: PredictorOutcomeType.VALUE,
      then: Yup.string().required("Select a numeric property to predict"),
      otherwise: Yup.string().notRequired(),
    }),
    aggregation: Yup.string().when("type", {
      is: PredictorOutcomeType.VALUE,
      then: Yup.string()
        .oneOf(Object.values(PredictorOutcomeAggregation))
        .required("Select an aggregation"),
      otherwise: Yup.string().notRequired(),
    }),
  });
  return Yup.object({
    audienceDefinition: Yup.object({
      conditions: Yup.array().optional(),
    }).required(),
    description: Yup.string().optional(),
    name: Yup.string().optional(),
    featureDefinitionId: Yup.string().required(),

    outcome:
      usingCatalog || usingLookalike
        ? Yup.object().notRequired()
        : outcomeSchema.required(),

    catalogId: usingCatalog
      ? Yup.string().required()
      : Yup.string().notRequired(),
    catalogDefinition: usingCatalog
      ? filterSchema.required()
      : Yup.object().notRequired(),

    baseAudienceId: usingLookalike
      ? Yup.string().required()
      : Yup.string().notRequired(),
  })
    .required("Predictor configuration is required")
    .typeError("Predictor must be a configured object.") as Yup.Schema<
    Omit<CreatePredictorInput, "parentModelId" | "featureDefinitionId">
  >;
}

// The snake_case keys mirror the trait form state (they predate the
// createTraitDefinition mutation's camelCase input, which call sites map to).
type CreateTraitFormValues = {
  name?: string | null;
  description?: string | null;
  segment_id?: string | number | null;
  relationship_id?: string | number | null;
  type?: string | null;
  config?: unknown;
  trait_template_id?: string | null;
  is_template?: boolean | null;
  predictorDefinition?: unknown;
  sourceType: string | undefined;
};

export const createTraitValidationSchema = Yup.lazy<CreateTraitFormValues>(
  (trait) => {
    const usingCatalog =
      "predictorDefinition" in trait &&
      Boolean((trait.predictorDefinition as any)?.catalogId);
    const usingLookalike =
      "predictorDefinition" in trait &&
      Boolean((trait.predictorDefinition as any)?.baseAudienceId);
    return Yup.object()
      .shape({
        name: traitNameValidator(trait.sourceType),
        description: Yup.string().nullable(),
        segment_id: Yup.string().required("Segment is required"),
        relationship_id:
          trait.type === TraitType.Formula ||
          trait.type === TraitType.Predictive ||
          trait.type === TraitType.TrackedAudienceMembership
            ? Yup.string().nullable()
            : Yup.string().required("Related model or event is required"),
        type: Yup.string().required("Related model or event is required"),
        config: Yup.object().required(),
        trait_template_id: Yup.string().nullable(),
        is_template: Yup.boolean().required(),
        ...(trait.type === TraitType.Predictive
          ? {
              predictorDefinition: getPredictorSchema(
                usingCatalog,
                usingLookalike,
              ),
            }
          : {}),

        // this is used for local form state not the server
        sourceType: Yup.string(),
      })
      .required();
  },
);
